import { IconEdit, IconTrash } from "@tabler/icons-react";
import { productCategoryApi } from "apis";
import { Loading } from "components";
import CustomTable from "components/ui/CustomTable";
import CustomText from "components/ui/CustomText/CustomText";
import GroupActions from "components/ui/GroupActions";
import { ActionItem } from "components/ui/GroupActions/GroupActions";
import { CommonParamsType } from "constants/types/common";
import { ProductCategoryResponse } from "constants/types/productCategory";
import { formatDate } from "utils/helpers/format";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "mantine-react-table";
import { FORM_IDS } from "pages/ProductCategory/config";
import { FC, useMemo, useRef, useCallback } from "react";
import { useSWRConfig } from "swr";
import { useNotification } from "utils/hooks";
import { useProductCategories } from "utils/hooks/request/useProductCategories";
import { getProductCategoryListKey } from "utils/hooks/request/useProductCategories";
import { useAppModal } from "utils/hooks/ui/useAppModal";
import EditForm from "../EditForm/EditForm";
import { useAbility } from "configs/casl";
import { canAccessRequirement } from "utils/helpers/authorization";

interface ProductCategoryTableProps {
  params: CommonParamsType;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const ICON_SIZE = 18;

const ProductCategoryTable: FC<ProductCategoryTableProps> = ({
  params,
  onPageChange,
  onLimitChange,
}) => {
  const { notification } = useNotification();
  const { mutate } = useSWRConfig();
  const ability = useAbility();

  const {
    productCategories,
    pagination,
    isLoading,
    mutate: localMutate,
  } = useProductCategories(params);

  const { openCrudModal, closeAll, closeCrudModal } = useAppModal();
  const activeModalId = useRef<string | null>(null);

  const handleDelete = useCallback(
    async (id: string) => {
      try {
        await productCategoryApi.delete(id);
        notification.success({ title: "Xóa nhóm mặt hàng thành công" });
        closeAll();
        localMutate();
        onPageChange(1);
      } catch {
        notification.error({ title: "Xóa nhóm mặt hàng thất bại" });
      }
    },
    [notification, closeAll, localMutate, onPageChange],
  );

  const handleOpenDeleteModal = useCallback(
    (id: string) => {
      openCrudModal(
        <CustomText variant="section-title">
          Xác nhận xóa nhóm mặt hàng
        </CustomText>,
        {
          mode: "delete",
          formId: FORM_IDS.DELETE,
          onConfirmDelete: async () => await handleDelete(id),
        },
      );
    },
    [openCrudModal, handleDelete],
  );

  const handleCloseModal = useCallback(() => {
    if (activeModalId.current) {
      closeCrudModal(activeModalId.current);
      activeModalId.current = null;
    }
  }, [closeCrudModal]);

  const handleOpenEditModal = useCallback(
    (category: ProductCategoryResponse) => {
      const modalId = openCrudModal(
        <CustomText variant="section-title">
          Chỉnh sửa nhóm mặt hàng
        </CustomText>,
        {
          mode: "edit",
          formId: FORM_IDS.EDIT,
          children: (
            <EditForm
              category={category}
              mutate={() => mutate(getProductCategoryListKey(params))}
              onClose={handleCloseModal}
            />
          ),
          submitText: "Cập nhật",
        },
        500,
      );
      activeModalId.current = modalId;
    },
    [openCrudModal, mutate, params, handleCloseModal],
  );

  const actions: ActionItem<ProductCategoryResponse>[] = useMemo(
    () => [
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <IconEdit size={ICON_SIZE} />,
        color: "orange",
        permission: "product_category:update",
        onClick: (row) => handleOpenEditModal(row),
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <IconTrash size={ICON_SIZE} />,
        color: "red",
        permission: "product_category:delete",
        onClick: (row) => handleOpenDeleteModal(row.id),
      },
    ],
    [handleOpenEditModal, handleOpenDeleteModal],
  );

  const hasActions = useMemo(
    () =>
      actions.some((a) => canAccessRequirement(ability, a.permission, true)),
    [actions, ability],
  );

  const columns: MRT_ColumnDef<ProductCategoryResponse>[] = [
    {
      id: "stt",
      header: "STT",
      size: 60,
      grow: false,
      Cell: ({ row }) => row.index + 1,
      mantineTableHeadCellProps: { align: "center" },
      mantineTableBodyCellProps: { align: "center" },
    },
    {
      accessorKey: "code",
      header: "Mã nhóm",
    },
    {
      accessorKey: "name",
      header: "Tên nhóm mặt hàng",
    },
    // {
    //   accessorKey: "description",
    //   header: "Mô tả",
    //   Cell: ({ cell }) => cell.getValue<string | null>() ?? "—",
    // },
    {
      accessorKey: "createdAt",
      header: "Ngày tạo",
      grow: false,
      Cell: ({ cell }) => formatDate(cell.getValue<number>()),
    },
    {
      accessorKey: "updatedAt",
      header: "Cập nhật lần cuối",
      grow: false,
      Cell: ({ cell }) => formatDate(cell.getValue<number>()),
    },
    ...(hasActions
      ? [
          {
            id: "actions",
            header: "Thao tác",
            enableSorting: false,
            enableColumnFilter: false,
            size: 80,
            grow: false,
            enableResizing: false,
            mantineTableHeadCellProps: { align: "center" },
            mantineTableBodyCellProps: { align: "center" },
            Cell: ({ row }: any) => (
              <GroupActions row={row.original} actions={actions} />
            ),
          } satisfies MRT_ColumnDef<ProductCategoryResponse>,
        ]
      : []),
  ];

  const handleChange = useCallback(
    (state: {
      page: number;
      limit: number;
      filters?: MRT_ColumnFiltersState;
    }) => {
      if (pagination && state.page !== pagination.page) {
        onPageChange(state.page);
      } else if (pagination && state.limit !== pagination.limit) {
        onLimitChange(state.limit);
      }
    },
    [onPageChange, onLimitChange, pagination],
  );

  return (
    <Loading variant="overlay" visible={isLoading}>
      <CustomTable
        columns={columns}
        data={productCategories ?? []}
        pagination={pagination!}
        onStateChange={handleChange}
      />
    </Loading>
  );
};

export default ProductCategoryTable;
